import {useState} from 'react'
import {StopIcon, XCircleFillIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash, Link} from '@primer/react'
import {ResolveConflictsButton} from '@github-ui/mergebox/components/sections/conflicts/ResolveConflictsButton'
import {ForYouItem} from '../shared/ForYouItem'
import {type MergeabilityData, useMergeabilityData} from './use-mergeability-data'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {useOpenMergeBoxPanel} from '../../mergebox/use-go-to-mergebox'

type MergeConflictsData = MergeabilityData & {
  conflictsSection: MergeabilityData['conflictsSection'] & {
    conflictsCondition: NonNullable<MergeabilityData['conflictsSection']['conflictsCondition']>
  }
}

export function shouldShowMergeConflicts(data: ReturnType<typeof useMergeabilityData>): data is MergeConflictsData {
  const conflictsSection = data?.conflictsSection
  const isConflictState =
    conflictsSection?.sectionStatus === 'HAS_CONFLICTS' || conflictsSection?.sectionStatus === 'HAS_REBASE_CONFLICTS'

  return (
    !!data &&
    !data.isClosedOrMerged &&
    // if the conflits section is stacked but has explicit conflicts, we still want to render this
    // for you item. shouldRender always returns false if the PR is stacked, so we need to check if
    // the PR itself has conflicts with its base branch. Because we don't want to change any mergebox
    // behavior, we make this change here.
    (!!conflictsSection?.shouldRender ||
      (!!conflictsSection?.isStacked && conflictsSection?.sectionStatus === 'HAS_CONFLICTS')) &&
    isConflictState &&
    !!conflictsSection.conflictsCondition
  )
}

// Renders when the branch has merge conflicts that must be resolved
export function MergeConflicts({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const openMergeBoxPanel = useOpenMergeBoxPanel()

  if (!shouldShowMergeConflicts(data)) return null

  const {mergeBoxPullRequest, conflictsSection, mergeSection} = data
  const {sectionStatus, conflictsCondition} = conflictsSection
  const conflictsWebEditorPath = `${mergeBoxPullRequest.resourcePath}/conflicts`
  const showAgentOption = mergeSection.canUserPushToBase && !!mergeBoxPullRequest.viewerCanUseCopilotCodingAgent
  const sendConflictAction = (actionName: 'edit_conflicts_on_web' | 'fix_conflicts_with_copilot') =>
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'merge-conflicts',
      action_name: actionName,
    })

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={XCircleFillIcon} variant="danger" emphasized />}
      emphasizedLeadingVisual
      title={'Merge conflicts'}
      itemName="merge-conflicts"
      padding="none"
      variant={variant}
      description={
        sectionStatus === 'HAS_CONFLICTS' ? (
          <span>
            Resolve the{' '}
            <Link as="button" inline onClick={event => openMergeBoxPanel(event.currentTarget)}>
              merge conflicts
            </Link>{' '}
            before this can merge.
          </span>
        ) : undefined
      }
      actionSlot={
        <>
          <ResolveConflictsButton
            conflictsWebEditorPath={conflictsWebEditorPath}
            conflictsState={sectionStatus}
            onError={setErrorMessage}
            onActionSelect={sendConflictAction}
            showAgentOption={showAgentOption}
            webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
            size="small"
          />
          <Button size="small" variant="default" inactive>
            Merge when ready
          </Button>
        </>
      }
    >
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </ForYouItem>
  )
}
